/**
 * Universal API error message extractor.
 *
 * Handles every error shape the backend returns:
 *  - string
 *  - { message }
 *  - { Message }
 *  - { error: string }
 *  - { error: { message } }
 *  - { error: { errors: string[] } }
 *  - { error: { errors: [{message}] } }   (array of error objects)
 *  - { error: { message: string[] } }     (array of messages)
 *  - { error: { data: { message } } }     (double-nested data body)
 *  - { data: string }                     (validation msgCode responses)
 *  - { data: { message } }               (nested data body on 200 responses)
 *  - HttpErrorResponse   (error.error.*)
 *  - Error instance       (error.message)
 *
 * Usage:
 *   import { extractErrorMessage } from '@core/utils/extract-error-message.util';
 *   catchError(err => {
 *     this.toastService.error('Error', extractErrorMessage(err));
 *     ...
 *   })
 */
export function extractErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred'
): string {
  if (error == null) return fallback;

  // Plain string
  if (typeof error === 'string') return error || fallback;

  // Narrow to object
  if (typeof error !== 'object') return fallback;

  const err = error as Record<string, any>;

  // ── HttpErrorResponse  ─────────────────────────────────────
  // error.error  is the parsed response body
  const body = err['error'];

  if (body != null && typeof body === 'object') {
    // { error: { errors: ['msg1', 'msg2'] } }  or  { error: { errors: [{message:'...'}] } }
    if (Array.isArray(body['errors']) && body['errors'].length) {
      const first = body['errors'][0];
      if (typeof first === 'string') {
        return body['errors'].filter(Boolean).join('. ') || fallback;
      }
      if (typeof first === 'object' && first !== null) {
        const msgs = body['errors']
          .map((e: any) => e?.message || e?.Message || e?.description || '')
          .filter(Boolean);
        if (msgs.length) return msgs.join('. ');
      }
    }

    // { error: { message: ['msg1', 'msg2'] } }   (array variant)
    if (Array.isArray(body['message']) && body['message'].length) {
      return body['message'].filter(Boolean).join('. ') || fallback;
    }

    // { error: { error: { [field]: ['msg1'] } } }  — Laravel field validation errors
    // e.g. { message: "Validation errors", error: { status: ["The selected status is invalid."] } }
    // Prefer specific field messages over the generic top-level message.
    if (body['error'] != null && typeof body['error'] === 'object' && !Array.isArray(body['error'])) {
      const fieldErrors = body['error'] as Record<string, any>;
      const fieldMsgs = (Object.values(fieldErrors) as any[])
        .filter(Array.isArray)
        .flat()
        .filter((v): v is string => typeof v === 'string' && Boolean(v));
      if (fieldMsgs.length) return fieldMsgs.join('. ');
    }

    // { error: { message: 'string' } }
    // If body also has a plain string 'error' field, combine both:
    // { message: "The CVV does not match.", error: "Card verification failed" }
    // → "The CVV does not match. Card verification failed"
    if (typeof body['message'] === 'string' && body['message']) {
      if (typeof body['error'] === 'string' && body['error'] && body['error'] !== body['message']) {
        const sep = body['message'].endsWith('.') || body['message'].endsWith('!') || body['message'].endsWith(':') ? ' ' : '. ';
        return `${body['message']}${sep}${body['error']}`;
      }
      return body['message'];
    }
    if (typeof body['Message'] === 'string' && body['Message']) {
      if (typeof body['error'] === 'string' && body['error'] && body['error'] !== body['Message']) {
        const sep = body['Message'].endsWith('.') || body['Message'].endsWith('!') || body['Message'].endsWith(':') ? ' ' : '. ';
        return `${body['Message']}${sep}${body['error']}`;
      }
      return body['Message'];
    }

    // { error: { error: 'string' } }  (double-nested, no message field)
    if (typeof body['error'] === 'string' && body['error']) {
      return body['error'];
    }

    // { error: { data: 'validation text' } }  (msgCode format)
    if (typeof body['data'] === 'string' && body['data']) {
      return body['data'];
    }

    // { error: { data: { message: '...' } } }  (double-nested data object)
    if (body['data'] != null && typeof body['data'] === 'object') {
      const dm = body['data']['message'] || body['data']['Message'];
      if (typeof dm === 'string' && dm) return dm;
    }
  }

  // { error: 'plain string' }
  // Combine with top-level message if both present:
  //   { message: "Getting error from carrier.", error: "Given Sim is Invalid or Carrier seems to down" }
  //   → "Getting error from carrier. Given Sim is Invalid or Carrier seems to down"
  if (typeof body === 'string' && body) {
    const topMsg = typeof err['message'] === 'string' ? err['message'] : '';
    if (topMsg && topMsg !== body) {
      const separator = topMsg.endsWith('.') || topMsg.endsWith('!') || topMsg.endsWith(':') ? ' ' : '. ';
      return `${topMsg}${separator}${body}`;
    }
    return body;
  }

  // ── Top-level properties ────────────────────────────────────
  // { message: ['...', '...'] }
  if (Array.isArray(err['message']) && err['message'].length) {
    return err['message'].filter(Boolean).join('. ') || fallback;
  }

  // { message: 'string', error: 'string' } — soft-error with both message and error
  // e.g. { Status: false, message: "Validation errors", error: "Already a plan change..." }
  // Prefer the specific error string over the generic message
  if (typeof err['message'] === 'string' && err['message']) {
    if (typeof err['error'] === 'string' && err['error'] && err['error'] !== err['message']) {
      return err['error'];
    }
    return err['message'];
  }
  if (typeof err['Message'] === 'string' && err['Message']) {
    if (typeof err['error'] === 'string' && err['error'] && err['error'] !== err['Message']) {
      return err['error'];
    }
    return err['Message'];
  }

  // { data: 'validation text' }   (msgCode = API001)
  if (typeof err['data'] === 'string' && err['data']) {
    return err['data'];
  }

  // { data: { message: '...' } }  — 200 body with nested message
  if (err['data'] != null && typeof err['data'] === 'object') {
    const dm = err['data']['message'] || err['data']['Message'];
    if (typeof dm === 'string' && dm) return dm;
  }

  // Error class instance
  if (err instanceof Error) {
    return err.message || fallback;
  }

  return fallback;
}

/**
 * Returns true if a 200-OK response body is a soft (business-logic) error.
 * Patterns: { success: false }, { Status: false }, { status: false }
 *
 * Usage:
 *   tap(res => { if (isSoftErrorResponse(res)) { ...handle... } })
 */
export function isSoftErrorResponse(response: unknown): boolean {
  if (response == null || typeof response !== 'object') return false;
  const r = response as Record<string, any>;
  return r['success'] === false || r['Status'] === false || r['status'] === false;
}

